import React, { useState, useRef } from "react";
import { Upload } from "lucide-react";
import { toast } from "react-toastify";
import { FileUploaderContainer, DropZone, DropZoneText, HiddenFileInput, FilesContainer, FileChip, FileName, RemoveButton } from "./styles";

const FileUploader = ({
  files = [],
  onFilesChange,
  acceptedFiles = "*/*",
  maxFileSize,
  disabled = false,
  placeholder = "Arrastra archivos aquí o haz clic para seleccionar",
  style,
  className,
  showIcon = true,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Validar si un archivo es aceptado
  const isFileAccepted = (file) => {
    if (acceptedFiles === "*/*") return true;

    const acceptedExtensions = acceptedFiles.split(",").map((ext) => ext.trim().toLowerCase());
    const fileExtension = `.${file.name.split(".").pop().toLowerCase()}`;

    return acceptedExtensions.includes(fileExtension);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
  };

  const handleNewFiles = (newFiles) => {
    const validFiles = [];
    const invalidFiles = [];

    newFiles.forEach((file) => {
      // Validar tipo de archivo
      if (!isFileAccepted(file)) {
        invalidFiles.push(file.name);
        return;
      }

      // Validar tamaño
      if (maxFileSize && file.size > maxFileSize) {
        toast.error(`El archivo ${file.name} es demasiado grande. Máximo ${formatFileSize(maxFileSize)}`);
        return;
      }

      validFiles.push(file);
    });

    // Mostrar error si hay archivos no válidos
    if (invalidFiles.length > 0) {
      const acceptedList = acceptedFiles
        .split(",")
        .map((ext) => ext.trim())
        .join(", ");
      toast.warning(`Archivos no válidos: ${invalidFiles.join(", ")}. Solo se permiten: ${acceptedList}`);
    }

    if (validFiles.length > 0) {
      const updatedFiles = [...files, ...validFiles];
      onFilesChange(updatedFiles);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleNewFiles(droppedFiles);
  };

  const handleFileInput = (e) => {
    if (disabled) return;

    const selectedFiles = Array.from(e.target.files);
    handleNewFiles(selectedFiles);

    // Limpiar el input para permitir seleccionar el mismo archivo nuevamente
    e.target.value = "";
  };

  const removeFile = (indexToRemove) => {
    const updatedFiles = files.filter((_, index) => index !== indexToRemove);
    onFilesChange(updatedFiles);
  };

  const openFileDialog = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <FileUploaderContainer style={style} className={className}>
      <DropZone
        isDragging={isDragging}
        className={disabled ? "disabled" : ""}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        {showIcon && <Upload size={20} style={{ marginBottom: "5px" }} />}
        <DropZoneText>
          <div className="secondary-text">{placeholder}</div>
          {maxFileSize > 0 && <div className="secondary-text">Máximo {formatFileSize(maxFileSize)} por archivo</div>}
        </DropZoneText>
      </DropZone>

      <HiddenFileInput ref={fileInputRef} type="file" multiple accept={acceptedFiles} onChange={handleFileInput} disabled={disabled} />

      {files.length > 0 && (
        <FilesContainer>
          {files.map((file, index) => (
            <FileChip key={`${file.name}-${file.size}-${file.lastModified || index}`}>
              <FileName title={file.name}>{file.name}</FileName>
              <RemoveButton
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(index);
                }}
                title="Eliminar archivo"
              >
                ×
              </RemoveButton>
            </FileChip>
          ))}
        </FilesContainer>
      )}
    </FileUploaderContainer>
  );
};

export default FileUploader;
